import * as React from 'react';

import type { ContractFieldsFragment } from 'bundles/enterprise-admin/queries/__generated__/ContractFields';
import type { CurriculumItem } from 'bundles/naptimejs/resources/programCurriculums.v1';

export type CollectionContentDraft = {
  programSlug: string;
  collectionId: string;
  badgeTemplateId?: string;
  addedContent: CurriculumItem[];
  removedContent: CurriculumItem[];
};

export type Contract = ContractFieldsFragment;

/**
 * This context is used to share admin-dashboard specific things between pages within the app.
 *
 * For example, we currently use the legacy context in AdminDashboardPage to share feature flags.
 * This new EnterpriseAdminContext is a replacement for that legacy context.
 */
export const EnterpriseAdminContext = React.createContext<{
  enableDataTechSku?: boolean;
  enableAssignContent?: boolean;
  hasBillableContracts?: boolean;
  hasNonBillableContracts?: boolean;
  hasSkillSets?: boolean;
  contracts?: Contract[];
  adminRoleType?: string;
  collectionContentDraft?: CollectionContentDraft;
  setCollectionContentDraft: React.Dispatch<React.SetStateAction<CollectionContentDraft | undefined>>;
}>({
  setCollectionContentDraft: () => {},
});

export const EnterpriseAdminContextProvider = ({
  enableDataTechSku,
  enableAssignContent,
  hasBillableContracts,
  hasNonBillableContracts,
  hasSkillSets,
  contracts,
  adminRoleType,
  children,
}: React.PropsWithChildren<{
  enableDataTechSku?: boolean;
  enableAssignContent?: boolean;
  hasBillableContracts?: boolean;
  hasNonBillableContracts?: boolean;
  hasSkillSets?: boolean;
  contracts?: Contract[];
  adminRoleType?: string;
}>) => {
  const [collectionContentDraft, setCollectionContentDraft] = React.useState<CollectionContentDraft | undefined>(
    undefined
  );

  return (
    <EnterpriseAdminContext.Provider
      value={{
        enableDataTechSku,
        enableAssignContent,
        hasBillableContracts,
        hasNonBillableContracts,
        hasSkillSets,
        contracts,
        collectionContentDraft,
        setCollectionContentDraft,
        adminRoleType,
      }}
    >
      {children}
    </EnterpriseAdminContext.Provider>
  );
};

export const useEnterpriseAdminContext = () => React.useContext(EnterpriseAdminContext);
